import React from 'react';
import { websocketService } from '../services/websocketService';
import ConnectionStatusIndicator from './ConnectionStatusIndicator';
import type { WebSocketConnection } from '../types/api';

interface ConnectionTableProps {
  connections: WebSocketConnection[];
  onDisconnect: (connectionId: string) => void;
  isLoading?: boolean;
}

const ConnectionTable: React.FC<ConnectionTableProps> = ({
  connections,
  onDisconnect,
  isLoading = false
}) => {
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const parseUserAgent = (userAgent: string) => {
    // Simple user agent parsing - in production, you might want to use a library
    const browser = userAgent.includes('Chrome') ? 'Chrome' :
                   userAgent.includes('Firefox') ? 'Firefox' :
                   userAgent.includes('Safari') ? 'Safari' :
                   userAgent.includes('Edge') ? 'Edge' : 'Unknown';
    
    const platform = userAgent.includes('Windows') ? 'Windows' :
                    userAgent.includes('Mac') ? 'macOS' :
                    userAgent.includes('Linux') ? 'Linux' :
                    userAgent.includes('Android') ? 'Android' :
                    userAgent.includes('iOS') ? 'iOS' : 'Unknown';
    
    return { browser, platform };
  };

  if (connections.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="text-4xl mb-4">🔌</div>
        <p className="text-lg font-medium">No active connections</p>
        <p className="text-sm">WebSocket connections will appear here when clients connect</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Connection
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User/Profile
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Client Info
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Duration
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Activity
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Messages
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {connections.map((connection) => {
            const clientInfo = parseUserAgent(connection.clientInfo.userAgent);
            
            return (
              <tr key={connection.id} className={isLoading ? 'opacity-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="mr-3">
                      <ConnectionStatusIndicator 
                        status={connection.status} 
                        size="sm" 
                        showText={false}
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 font-mono">
                        {connection.id.substring(0, 8)}...
                      </div>
                      <div className="text-xs text-gray-500">
                        {connection.subscriptions.length} subscription{connection.subscriptions.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {connection.userId ? (
                      <div>
                        <div className="font-medium">User</div>
                        <div className="text-xs text-gray-500 font-mono">
                          {connection.userId.substring(0, 8)}...
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">Anonymous</span>
                    )}
                  </div>
                  {connection.profileId && (
                    <div className="text-sm text-gray-500 mt-1">
                      <div className="text-xs font-medium">Profile</div>
                      <div className="text-xs font-mono">
                        {connection.profileId.substring(0, 8)}...
                      </div>
                    </div>
                  )}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div className="flex items-center">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {connection.clientInfo.ip}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {clientInfo.browser} • {clientInfo.platform}
                    </div>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {websocketService.formatConnectionDuration(connection.connectionTime)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Connected at {new Date(connection.connectionTime).toLocaleTimeString()}
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {websocketService.formatLastActivity(connection.lastActivity)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Last: {new Date(connection.lastActivity).toLocaleTimeString()}
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm">
                    <div className="flex items-center text-green-600">
                      <span className="mr-1">↑</span>
                      <span>{formatNumber(connection.messageCount.sent)}</span>
                    </div>
                    <div className="flex items-center text-blue-600">
                      <span className="mr-1">↓</span>
                      <span>{formatNumber(connection.messageCount.received)}</span>
                    </div>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onDisconnect(connection.id)}
                      disabled={isLoading}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Disconnect this connection"
                    >
                      🔌 Disconnect
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
      {/* Table Footer with Summary */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>
            Showing {connections.length} active connection{connections.length !== 1 ? 's' : ''}
          </span>
          <div className="flex space-x-4">
            <span>
              🟢 {connections.filter(c => c.status === 'connected').length} Connected
            </span>
            <span>
              🟡 {connections.filter(c => c.status === 'reconnecting').length} Reconnecting
            </span>
            <span>
              🔴 {connections.filter(c => c.status === 'disconnected').length} Disconnected
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionTable;
